import { extractErrorMessage, suggestionName } from "../utils/acceptSuggestions";
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  BookOpen, Users, Award, Shield, ChevronRight, ChevronLeft,
  Check, Loader2, RefreshCw, Download, Mail, AlertTriangle,
  GraduationCap, Target, Clock, CheckCircle2, XCircle, FileText,
  UserCheck, Settings, Play, BarChart3, Bell, Send, FlaskConical, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import StepDialog from '../components/StepDialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useAuth } from '../context/AuthContext';
import AISuggestionsBanner from '../components/AISuggestionsBanner';
import PolicyComplianceDashboard from '../components/PolicyComplianceDashboard';
import ImpactSimulator from '../components/ImpactSimulator';

const LEVEL_COLORS = {
  level1_awareness: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  level2_functional: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
  level3_strategic: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' }
};

const STATUS_COLORS = {
  not_started: 'bg-slate-100 text-slate-600',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  certified: 'bg-sky-100 text-sky-700',
  expired: 'bg-red-100 text-red-700'
};

export default function TrainingAwarenessPage() {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  // Training modules manager (AI-accept → draft → review/edit → publish)
  const [modules, setModules] = useState([]);
  const [acceptingTraining, setAcceptingTraining] = useState(false);
  const [showModuleEditor, setShowModuleEditor] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [editorForm, setEditorForm] = useState({ title: "", description: "", content: "", duration_minutes: 30, status: "draft" });
  const [moduleSaving, setModuleSaving] = useState(false);
  const [curriculum, setCurriculum] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [stats, setStats] = useState(null);
  const [tracker, setTracker] = useState(null);
  const [templates, setTemplates] = useState(null);
  const [wizardState, setWizardState] = useState(null);
  
  // Dialog states
  const [showCurriculumDialog, setShowCurriculumDialog] = useState(false);
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizStep, setQuizStep] = useState(0);
  const [settingsStep, setSettingsStep] = useState(0);
  const [showWizardDialog, setShowWizardDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Notification states
  const [notifications, setNotifications] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState({
    enabled: true,
    reminder_days_before: [7, 3, 1],
    escalation_days_overdue: [1, 3, 7],
    escalation_recipients: [],
    auto_reminder_enabled: true,
    daily_digest_enabled: false,
    digest_time: "09:00"
  });
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [newEscalationEmail, setNewEscalationEmail] = useState('');

  // V14 D5 — AI Suggest Modules dialog state
  const [showAiSuggestDialog, setShowAiSuggestDialog] = useState(false);
  const [aiSuggestRole, setAiSuggestRole] = useState('process_manager');
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  
  // Wizard form state
  const [wizardSettings, setWizardSettings] = useState({
    level1_deadline_days: 30,
    level2_simulation_frequency: 'bi-annually',
    level3_simulation_frequency: 'annually',
    training_prerequisite_for_approval: true,
    kpis: {
      completion_rate_target: 95,
      max_drill_response_time_minutes: 10,
      recertification_months: 12
    }
  });

  const isAdmin = user?.role === 'bcm_manager' || user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const [curriculumRes, assignmentsRes, statsRes] = await Promise.all([
        api.get('/bia-engine/training/curriculum'),
        api.get('/bia-engine/training/assignments'),
        api.get('/bia-engine/training/stats')
      ]);
      
      setCurriculum(curriculumRes.data.curriculum);
      setAssignments(assignmentsRes.data.assignments);
      setStats(statsRes.data);
      
      if (isAdmin) {
        const [trackerRes, templatesRes, wizardRes, notifRes, notifSettingsRes] = await Promise.all([
          api.get('/bia-engine/training/competency-tracker'),
          api.get('/bia-engine/training/communication-templates'),
          api.get('/bia-engine/training/competency/wizard/state'),
          api.get('/bia-engine/training/notifications/pending'),
          api.get('/bia-engine/training/notifications/settings')
        ]);
        setTracker(trackerRes.data);
        setTemplates(templatesRes.data.templates);
        setWizardState(wizardRes.data);
        setNotifications(notifRes.data);
        setNotificationSettings(notifSettingsRes.data);
        
        if (wizardRes.data.settings) {
          setWizardSettings(prev => ({
            ...prev,
            ...wizardRes.data.settings
          }));
        }
      }
    } catch (err) {
      toast.error('Failed to load training data');
    } finally {
      setLoading(false);
    }
  }, [api, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchModules = useCallback(async () => {
    try {
      const res = await api.get('/training/modules');
      setModules(res.data || []);
    } catch (err) {
      console.error('Failed to fetch training modules:', err);
    }
  }, [api]);

  useEffect(() => {
    if (isAdmin) fetchModules();
  }, [isAdmin, fetchModules]);

  // AI-accept: create DRAFT modules (server generates enrichment-based content). The
  // banner handles per-item badges + dedup; this handler just creates + refreshes.
  const handleAcceptTrainingSuggestions = useCallback(async (items) => {
    setAcceptingTraining(true);
    const list = items || [];
    const total = list.length;
    if (total > 1) toast.info(`Generating ${total} training modules — this can take a moment…`);
    let created = 0; let error;
    for (const raw of list) {
      const o = raw && typeof raw === 'object' ? raw : {};
      const name = o.module_name || suggestionName(raw);
      try {
        await api.post('/training/modules/create-from-suggestion', {
          module_name: name,
          regulatory_driver: o.regulatory_driver || null,
          target_audience: o.target_audience || null,
          depth: o.depth || null,
        });
        created += 1;
        if (total > 1) toast.success(`Created ${created}/${total}: ${name}`);
      } catch (e) {
        error = extractErrorMessage(e, 'Some modules could not be created');
      }
    }
    await fetchModules();
    setAcceptingTraining(false);
    return { created, error };
  }, [api, fetchModules]);

  const openModuleEditor = (m) => {
    setEditingModule(m);
    setEditorForm({
      title: m.title || '',
      description: m.description || '',
      content: m.content || '',
      duration_minutes: m.duration_minutes || 30,
      status: m.status || 'draft',
    });
    setShowModuleEditor(true);
  };

  const saveModule = async (publish) => {
    if (!editingModule) return;
    setModuleSaving(true);
    try {
      await api.put(`/training/modules/${editingModule.id}`, {
        title: editorForm.title,
        description: editorForm.description,
        content: editorForm.content,
        duration_minutes: Number(editorForm.duration_minutes) || 30,
        ...(publish ? { status: 'active' } : {}),
      });
      toast.success(publish ? 'Module published' : 'Draft saved');
      setShowModuleEditor(false);
      await fetchModules();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to save module'));
    } finally {
      setModuleSaving(false);
    }
  };

  const handleAutoAssign = async () => {
    setSaving(true);
    try {
      const res = await api.post('/bia-engine/training/assignments/auto-assign');
      toast.success(res.data.message);
      await fetchData();
    } catch (err) {
      toast.error('Failed to auto-assign training');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWizardSettings = async () => {
    setSaving(true);
    try {
      await api.post('/bia-engine/training/competency/wizard/settings', wizardSettings);
      toast.success('Competency settings saved');
      setShowWizardDialog(false);
      await fetchData();
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotificationSettings = async () => {
    setSaving(true);
    try {
      await api.post('/bia-engine/training/notifications/settings', notificationSettings);
      toast.success('Notification settings saved');
      setShowNotificationSettings(false);
      await fetchData();
    } catch (err) {
      toast.error('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSendReminder = async (assignmentId) => {
    setSaving(true);
    try {
      const res = await api.post(`/bia-engine/training/notifications/send-reminder?assignment_id=${assignmentId}`);
      toast.success(res.data.message);
      await fetchData();
    } catch (err) {
      toast.error('Failed to send reminder');
    } finally {
      setSaving(false);
    }
  };

  const handleSendBulkReminders = async (type) => {
    setSaving(true);
    try {
      const res = await api.post(`/bia-engine/training/notifications/send-bulk-reminders?notification_type=${type}`);
      toast.success(`${res.data.sent_count} reminders sent`);
      await fetchData();
    } catch (err) {
      toast.error('Failed to send bulk reminders');
    } finally {
      setSaving(false);
    }
  };

  const handleEscalate = async (assignmentId) => {
    if (!window.confirm('Escalate this overdue training to management? This action will be logged in the audit trail.')) {
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post(`/bia-engine/training/notifications/escalate?assignment_id=${assignmentId}`);
      toast.success('Training escalated to management');
      await fetchData();
    } catch (err) {
      toast.error('Failed to escalate');
    } finally {
      setSaving(false);
    }
  };

  const addEscalationRecipient = () => {
    if (newEscalationEmail && !notificationSettings.escalation_recipients.includes(newEscalationEmail)) {
      setNotificationSettings(prev => ({
        ...prev,
        escalation_recipients: [...prev.escalation_recipients, newEscalationEmail]
      }));
      setNewEscalationEmail('');
    }
  };

  const removeEscalationRecipient = (email) => {
    setNotificationSettings(prev => ({
      ...prev,
      escalation_recipients: prev.escalation_recipients.filter(e => e !== email)
    }));
  };

  const handleStartTraining = async (assignmentId) => {
    try {
      await api.post(`/bia-engine/training/assignments/${assignmentId}/start`);
      toast.success('Training started');
      await fetchData();
    } catch (err) {
      toast.error('Failed to start training');
    }
  };

  const handleTakeQuiz = async (level) => {
    try {
      const res = await api.get(`/bia-engine/training/knowledge-check/${level}`);
      setQuizQuestions(res.data.questions);
      setSelectedLevel(level);
      setQuizAnswers({});
      setQuizResult(null);
      setShowQuizDialog(true);
    } catch (err) {
      toast.error('Failed to load quiz');
    }
  };

  const handleSubmitQuiz = async () => {
    const answers = quizQuestions.map((_, idx) => quizAnswers[idx] || '');
    
    if (answers.some(a => !a)) {
      toast.error('Please answer all questions');
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post(`/bia-engine/training/knowledge-check/${selectedLevel}/submit`, answers);
      setQuizResult(res.data);
      
      // If passed, find and complete the assignment
      if (res.data.passed) {
        const assignment = assignments.find(a => a.training_level === selectedLevel && a.user_id === user.id);
        if (assignment) {
          await api.post(`/bia-engine/training/assignments/${assignment.id}/complete?score=${res.data.score}`);
          toast.success('Training completed and certified!');
          await fetchData();
        }
      }
    } catch (err) {
      toast.error('Failed to submit quiz');
    } finally {
      setSaving(false);
    }
  };

  const viewCurriculum = (level) => {
    setSelectedLevel(level);
    setShowCurriculumDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="training-loading">
        <Loader2 className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  const myAssignments = assignments.filter(a => a.user_id === user?.id);

  return (
    <div className="space-y-6" data-testid="training-awareness-page">
      {isAdmin ? (
        <AISuggestionsBanner module="training_awareness"
          onAccept={handleAcceptTrainingSuggestions}
          existingKeys={modules.map((m) => m.title)}
          itemKeyOf={(raw) => (raw?.module_name || raw?.name || String(raw ?? ""))} />
      ) : (
        <AISuggestionsBanner module="training_awareness" />
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-emerald-100 text-emerald-700">DO Phase</Badge>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">ISO 22301:7.2-7.3</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <GraduationCap className="w-7 h-7 text-sky-700" />
            Training & Awareness
          </h1>
          <p className="text-slate-500 mt-1">BCM competency program for recovery readiness</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowWizardDialog(true)} data-testid="competency-wizard-btn">
              <Settings className="w-4 h-4 mr-2" />
              Competency Settings
            </Button>
            <Button onClick={handleAutoAssign} disabled={saving} data-testid="auto-assign-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCheck className="w-4 h-4 mr-2" />}
              Auto-Assign Training
            </Button>
            <Button
              onClick={() => setShowAiSuggestDialog(true)}
              variant="outline"
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="ai-suggest-modules-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              AI Suggest Modules
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Assignments</p>
                  <p className="text-2xl font-bold">{stats.total_assignments}</p>
                </div>
                <Users className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Completion Rate</p>
                  <p className="text-2xl font-bold text-emerald-600">{stats.completion_rate}%</p>
                </div>
                <BarChart3 className="w-8 h-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Certified</p>
                  <p className="text-2xl font-bold text-sky-700">{stats.certified_count}</p>
                </div>
                <Award className="w-8 h-8 text-sky-500" />
              </div>
            </CardContent>
          </Card>
          <Card className={stats.overdue_count > 0 ? 'border-red-200' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Overdue</p>
                  <p className={`text-2xl font-bold ${stats.overdue_count > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                    {stats.overdue_count}
                  </p>
                </div>
                <AlertTriangle className={`w-8 h-8 ${stats.overdue_count > 0 ? 'text-red-400' : 'text-slate-400'}`} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.by_status?.in_progress || 0}</p>
                </div>
                <Play className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="policy-compliance" className="space-y-4">
        <TabsList className="bg-white border">
          <TabsTrigger value="policy-compliance" data-testid="policy-compliance-tab">
            <Shield className="w-4 h-4 mr-2" />
            Policy Compliance
          </TabsTrigger>
          <TabsTrigger value="my-training" data-testid="my-training-tab">
            <BookOpen className="w-4 h-4 mr-2" />
            My Training
          </TabsTrigger>
          <TabsTrigger value="curriculum" data-testid="curriculum-tab">
            <FileText className="w-4 h-4 mr-2" />
            Curriculum
          </TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="tracker" data-testid="tracker-tab">
                <Users className="w-4 h-4 mr-2" />
                Competency Tracker
              </TabsTrigger>
              <TabsTrigger value="notifications" data-testid="notifications-tab">
                <Bell className="w-4 h-4 mr-2" />
                Notifications
                {notifications?.summary?.total_pending > 0 && (
                  <Badge className="ml-2 bg-red-500 text-white text-xs">{notifications.summary.total_pending}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="communications" data-testid="communications-tab">
                <Mail className="w-4 h-4 mr-2" />
                Communications
              </TabsTrigger>
              <TabsTrigger value="simulator" data-testid="simulator-tab">
                <FlaskConical className="w-4 h-4 mr-2" />
                Impact Simulator
              </TabsTrigger>
              <TabsTrigger value="modules" data-testid="modules-tab">
                <BookOpen className="w-4 h-4 mr-2" />
                Modules
                {modules.filter((m) => m.status === "draft").length > 0 && (
                  <Badge className="ml-2 bg-amber-500 text-white text-xs">{modules.filter((m) => m.status === "draft").length}</Badge>
                )}
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {isAdmin && (
          <TabsContent value="modules">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-sky-700" />
                  Training Modules
                </CardTitle>
                <CardDescription>
                  AI-drafted and authored modules. Review the content and publish to make a module active.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {modules.length === 0 ? (
                  <p className="text-sm text-slate-400 py-8 text-center">
                    No modules yet. Accept an AI suggestion above to create a draft module.
                  </p>
                ) : (
                  <div className="space-y-2" data-testid="training-modules-list">
                    {modules.map((m) => (
                      <div key={m.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50" data-testid={`training-module-${m.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-900">{m.title}</span>
                            <Badge className={m.status === "draft" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"} data-testid={`training-module-status-${m.id}`}>
                              {m.status === "draft" ? "Draft" : "Active"}
                            </Badge>
                            {m.source === "ai_suggestion" && <Badge variant="outline" className="text-[10px]">AI</Badge>}
                          </div>
                          <p className="text-xs text-slate-500 truncate">{m.description}</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => openModuleEditor(m)} data-testid={`training-module-edit-${m.id}`}>
                          <FileText className="w-3.5 h-3.5 mr-1" />
                          Review &amp; Edit
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Policy Compliance Tab (Default) */}
        <TabsContent value="policy-compliance">
          <PolicyComplianceDashboard />
        </TabsContent>

        {/* Impact Simulator Tab */}
        <TabsContent value="simulator">
          <ImpactSimulator />
        </TabsContent>

        {/* My Training Tab */}
        <TabsContent value="my-training">
          <Card>
            <CardHeader>
              <CardTitle>My Training Assignments</CardTitle>
              <CardDescription>
                Complete your assigned BCM training to become recovery-ready
              </CardDescription>
            </CardHeader>
            <CardContent>
              {myAssignments.length === 0 ? (
                <div className="text-center py-12">
                  <GraduationCap className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-2">No training assigned yet</p>
                  <p className="text-sm text-slate-400">Contact your BCM administrator for training assignments</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {myAssignments.map(assignment => {
                    const levelData = curriculum?.[assignment.training_level];
                    const colors = LEVEL_COLORS[assignment.training_level] || LEVEL_COLORS.level1_awareness;
                    
                    return (
                      <Card key={assignment.id} className={`border-l-4 ${colors.border}`}>
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h4 className="font-semibold">{levelData?.name || assignment.training_level}</h4>
                                <Badge className={STATUS_COLORS[assignment.status]}>
                                  {assignment.status.replace('_', ' ')}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-600 mb-2">{levelData?.subtitle}</p>
                              <div className="flex items-center gap-4 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {levelData?.duration}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Target className="w-3 h-3" />
                                  Due: {new Date(assignment.due_date).toLocaleDateString()}
                                </span>
                                {assignment.score !== null && (
                                  <span className="flex items-center gap-1">
                                    <Award className="w-3 h-3" />
                                    Score: {assignment.score}%
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={() => viewCurriculum(assignment.training_level)}>
                                View Curriculum
                              </Button>
                              {assignment.status === 'not_started' && (
                                <Button size="sm" onClick={() => handleStartTraining(assignment.id)}>
                                  <Play className="w-4 h-4 mr-1" />
                                  Start
                                </Button>
                              )}
                              {assignment.status === 'in_progress' && (
                                <Button size="sm" onClick={() => handleTakeQuiz(assignment.training_level)}>
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Take Quiz
                                </Button>
                              )}
                              {assignment.status === 'certified' && (
                                <Badge variant="default" className="bg-sky-700">
                                  <Award className="w-3 h-3 mr-1" />
                                  Certified
                                </Badge>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Curriculum Tab */}
        <TabsContent value="curriculum">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {curriculum && Object.entries(curriculum).map(([level, data]) => {
              const colors = LEVEL_COLORS[level] || LEVEL_COLORS.level1_awareness;
              const Icon = level === 'level1_awareness' ? Users : level === 'level2_functional' ? Shield : Award;
              
              return (
                <Card key={level} className={`${colors.border} border-2`}>
                  <CardHeader>
                    <div className={`w-12 h-12 rounded-lg ${colors.bg} flex items-center justify-center mb-3`}>
                      <Icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <CardTitle className="text-lg">{data.name}</CardTitle>
                    <CardDescription>{data.subtitle}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Audience</p>
                      <p className="text-sm text-slate-600">{data.audience}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">Duration</p>
                      <p className="text-sm text-slate-600">{data.duration}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">Delivery</p>
                      <p className="text-sm text-slate-600">{data.delivery}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Key Topics</p>
                      <ul className="space-y-1">
                        {data.key_topics?.slice(0, 4).map((topic, idx) => (
                          <li key={idx} className="text-xs text-slate-600 flex items-start gap-2">
                            <ChevronRight className="w-3 h-3 mt-0.5 text-slate-400" />
                            {topic.topic}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="pt-2 flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => viewCurriculum(level)}>
                        Full Details
                      </Button>
                      <Button size="sm" className="flex-1" onClick={() => handleTakeQuiz(level)}>
                        Practice Quiz
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Competency Tracker Tab (Admin) */}
        {isAdmin && (
          <TabsContent value="tracker">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Competency Tracker</CardTitle>
                    <CardDescription>Track training status and certifications</CardDescription>
                  </div>
                  <Button variant="outline" onClick={fetchData}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Untrained Process Owners Warning */}
                {tracker?.untrained_process_owners?.length > 0 && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-red-800">High Risk - Untrained Process Owners</h4>
                        <p className="text-sm text-red-600 mt-1">
                          The following process owners have not completed Level 2 training:
                        </p>
                        <ul className="mt-2 space-y-1">
                          {tracker.untrained_process_owners.map((owner, idx) => (
                            <li key={idx} className="text-sm text-red-700">
                              • {owner.user_name} - {owner.process_name} ({owner.status})
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Training Level</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Expiry</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tracker?.tracker?.map((record, idx) => (
                      <TableRow key={record.user_id || record.user_name || idx}>
                        <TableCell className="font-medium">
                          {record.user_name}
                          {record.is_process_owner && (
                            <Badge variant="outline" className="ml-2 text-xs">Process Owner</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{record.user_email}</TableCell>
                        <TableCell>
                          <Badge className={LEVEL_COLORS[record.training_level]?.bg + ' ' + LEVEL_COLORS[record.training_level]?.text}>
                            {record.training_level_name?.split(':')[0]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[record.status]}>
                            {record.status?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {record.due_date ? new Date(record.due_date).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>{record.score !== null ? `${record.score}%` : '-'}</TableCell>
                        <TableCell className="text-sm">
                          {record.certification_expiry ? new Date(record.certification_expiry).toLocaleDateString() : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Notifications Tab (Admin) */}
        {isAdmin && (
          <TabsContent value="notifications">
            <div className="space-y-6">
              {/* Notification Summary */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Training Notifications</h3>
                  <p className="text-sm text-slate-500">Send reminders and escalate overdue training</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowNotificationSettings(true)}>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </Button>
                  <Button 
                    onClick={() => handleSendBulkReminders('all')} 
                    disabled={saving || (notifications?.summary?.total_pending === 0)}
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Send All Reminders ({notifications?.summary?.total_pending || 0})
                  </Button>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className={notifications?.summary?.reminders_count > 0 ? 'border-amber-200' : ''}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Upcoming Reminders</p>
                        <p className={`text-2xl font-bold ${notifications?.summary?.reminders_count > 0 ? 'text-amber-600' : 'text-slate-600'}`}>
                          {notifications?.summary?.reminders_count || 0}
                        </p>
                      </div>
                      <Clock className={`w-8 h-8 ${notifications?.summary?.reminders_count > 0 ? 'text-amber-400' : 'text-slate-400'}`} />
                    </div>
                  </CardContent>
                </Card>
                <Card className={notifications?.summary?.overdue_count > 0 ? 'border-red-200' : ''}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Overdue</p>
                        <p className={`text-2xl font-bold ${notifications?.summary?.overdue_count > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                          {notifications?.summary?.overdue_count || 0}
                        </p>
                      </div>
                      <AlertTriangle className={`w-8 h-8 ${notifications?.summary?.overdue_count > 0 ? 'text-red-400' : 'text-slate-400'}`} />
                    </div>
                  </CardContent>
                </Card>
                <Card className={notifications?.summary?.escalations_needed > 0 ? 'border-purple-200' : ''}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Need Escalation</p>
                        <p className={`text-2xl font-bold ${notifications?.summary?.escalations_needed > 0 ? 'text-purple-600' : 'text-slate-600'}`}>
                          {notifications?.summary?.escalations_needed || 0}
                        </p>
                      </div>
                      <Bell className={`w-8 h-8 ${notifications?.summary?.escalations_needed > 0 ? 'text-purple-400' : 'text-slate-400'}`} />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Notifications</p>
                        <p className="text-2xl font-bold text-slate-600">
                          {notificationSettings.enabled ? 'Enabled' : 'Disabled'}
                        </p>
                      </div>
                      <Settings className="w-8 h-8 text-slate-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Overdue Training - Priority */}
              {notifications?.overdue?.length > 0 && (
                <Card className="border-red-200">
                  <CardHeader>
                    <CardTitle className="text-red-700 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Overdue Training
                    </CardTitle>
                    <CardDescription>Users who have missed their training deadline</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Training Level</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Days Overdue</TableHead>
                          <TableHead>Severity</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {notifications.overdue.map(item => (
                          <TableRow key={item.assignment_id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{item.user_name}</p>
                                <p className="text-xs text-slate-500">{item.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={LEVEL_COLORS[item.training_level]?.bg + ' ' + LEVEL_COLORS[item.training_level]?.text}>
                                {item.training_level_name?.split(':')[0] || item.training_level}
                              </Badge>
                            </TableCell>
                            <TableCell>{new Date(item.due_date).toLocaleDateString()}</TableCell>
                            <TableCell className="font-bold text-red-600">{item.days_overdue} days</TableCell>
                            <TableCell>
                              <Badge variant={item.severity === 'critical' ? 'destructive' : item.severity === 'high' ? 'default' : 'secondary'}>
                                {item.severity}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => handleSendReminder(item.assignment_id)} disabled={saving}>
                                  <Mail className="w-3 h-3 mr-1" />
                                  Remind
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => handleEscalate(item.assignment_id)} disabled={saving}>
                                  <Bell className="w-3 h-3 mr-1" />
                                  Escalate
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Upcoming Reminders */}
              {notifications?.reminders?.length > 0 && (
                <Card className="border-amber-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-amber-700 flex items-center gap-2">
                          <Clock className="w-5 h-5" />
                          Upcoming Deadlines
                        </CardTitle>
                        <CardDescription>Training deadlines approaching</CardDescription>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => handleSendBulkReminders('upcoming')} disabled={saving}>
                        <Send className="w-4 h-4 mr-2" />
                        Send All Reminders
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Training Level</TableHead>
                          <TableHead>Due Date</TableHead>
                          <TableHead>Days Remaining</TableHead>
                          <TableHead>Urgency</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {notifications.reminders.map(item => (
                          <TableRow key={item.assignment_id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{item.user_name}</p>
                                <p className="text-xs text-slate-500">{item.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={LEVEL_COLORS[item.training_level]?.bg + ' ' + LEVEL_COLORS[item.training_level]?.text}>
                                {item.training_level_name?.split(':')[0] || item.training_level}
                              </Badge>
                            </TableCell>
                            <TableCell>{new Date(item.due_date).toLocaleDateString()}</TableCell>
                            <TableCell className={item.days_remaining <= 1 ? 'font-bold text-red-600' : item.days_remaining <= 3 ? 'font-bold text-amber-600' : ''}>
                              {item.days_remaining} days
                            </TableCell>
                            <TableCell>
                              <Badge variant={item.urgency === 'urgent' ? 'destructive' : item.urgency === 'soon' ? 'default' : 'secondary'}>
                                {item.urgency}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button size="sm" variant="outline" onClick={() => handleSendReminder(item.assignment_id)} disabled={saving}>
                                <Mail className="w-3 h-3 mr-1" />
                                Send Reminder
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* No Notifications State */}
              {(!notifications?.reminders?.length && !notifications?.overdue?.length) && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
                    <p className="text-emerald-600 font-medium">All caught up!</p>
                    <p className="text-sm text-slate-500">No pending training notifications</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        )}

        {/* Communications Tab (Admin) */}
        {isAdmin && templates && (
          <TabsContent value="communications">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(templates).map(([key, template]) => (
                <Card key={key}>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Mail className="w-4 h-4 text-sky-700" />
                      {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-slate-500">Subject</Label>
                        <p className="text-sm font-medium">{template.subject}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Preview</Label>
                        <div className="text-sm text-slate-600 bg-slate-50 p-3 rounded max-h-40 overflow-auto whitespace-pre-wrap">
                          {template.body.substring(0, 300)}...
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="w-full">
                        <Download className="w-4 h-4 mr-2" />
                        Copy Template
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Curriculum Details Dialog */}
      <Dialog open={showCurriculumDialog} onOpenChange={setShowCurriculumDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedLevel && curriculum?.[selectedLevel] && (
            <>
              <DialogHeader>
                <DialogTitle>{curriculum[selectedLevel].name}</DialogTitle>
                <DialogDescription>{curriculum[selectedLevel].subtitle}</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                <div>
                  <h4 className="font-medium mb-2">Learning Objectives</h4>
                  <ul className="space-y-2">
                    {curriculum[selectedLevel].learning_objectives?.map((obj, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5" />
                        {obj}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Topics Covered</h4>
                  <div className="space-y-2">
                    {curriculum[selectedLevel].key_topics?.map((topic, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{topic.topic}</span>
                          <Badge variant="outline">{topic.duration}</Badge>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">{topic.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCurriculumDialog(false)}>Close</Button>
                <Button onClick={() => { setShowCurriculumDialog(false); handleTakeQuiz(selectedLevel); }}>
                  Take Knowledge Check
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Quiz Dialog */}
      <StepDialog
        open={showQuizDialog}
        onOpenChange={(open) => { setShowQuizDialog(open); if (!open) { setQuizResult(null); setQuizStep(0); } }}
        title={`Knowledge Check — ${curriculum?.[selectedLevel]?.name || ""}`}
        steps={quizResult
          ? [{ id: "results", title: "Results", subtitle: `Score: ${quizResult.score}%`, isComplete: true }]
          : quizQuestions.map((q, i) => ({
              id: `q${i}`,
              title: `Q${i + 1}`,
              subtitle: q.question?.substring(0, 50) + (q.question?.length > 50 ? "..." : ""),
              isComplete: quizAnswers[i] !== undefined,
            }))
        }
        currentStep={quizStep}
        onStepChange={setQuizStep}
        onSubmit={!quizResult ? handleSubmitQuiz : () => { setShowQuizDialog(false); setQuizResult(null); setQuizStep(0); }}
        onCancel={() => { setShowQuizDialog(false); setQuizResult(null); setQuizStep(0); }}
        submitLabel={!quizResult ? (saving ? "Submitting..." : "Submit All Answers") : "Close"}
        submitDisabled={saving}
        saving={saving}
        canAdvance={true}
        maxWidth="max-w-2xl"
      >
        {!quizResult ? (
          <div className="py-4">
            {quizQuestions[quizStep] && (
              <div className="space-y-3">
                <p className="font-medium text-sm">
                  {quizStep + 1}. {quizQuestions[quizStep].question}
                </p>
                <div className="space-y-2">
                  {quizQuestions[quizStep].options.map((opt, optIdx) => (
                    <div
                      key={optIdx}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        quizAnswers[quizStep] === opt.charAt(0)
                          ? 'border-sky-700 bg-sky-50'
                          : 'border-slate-200 hover:border-sky-300'
                      }`}
                      onClick={() => setQuizAnswers(prev => ({ ...prev, [quizStep]: opt.charAt(0) }))}
                      data-testid={`quiz-option-${optIdx}`}
                    >
                      <span className="text-sm">{opt}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6 py-4">
            <div className={`p-6 rounded-lg text-center ${quizResult.passed ? 'bg-emerald-50' : 'bg-red-50'}`}>
              {quizResult.passed ? (
                <Award className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
              ) : (
                <XCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />
              )}
              <h3 className={`text-xl font-bold ${quizResult.passed ? 'text-emerald-700' : 'text-red-700'}`}>
                {quizResult.passed ? 'Congratulations!' : 'Not Quite'}
              </h3>
              <p className="text-lg mt-1">Score: {quizResult.score}%</p>
              <p className="text-sm text-slate-600 mt-2">
                {quizResult.correct_count} of {quizResult.total_questions} correct
              </p>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium">Review Answers</h4>
              {quizResult.results.map((result, idx) => (
                <div key={idx} className={`p-3 rounded-lg ${result.is_correct ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  <div className="flex items-start gap-2">
                    {result.is_correct ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium">{result.question}</p>
                      <p className="text-xs text-slate-600 mt-1">
                        Your answer: {result.your_answer} | Correct: {result.correct_answer}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{result.explanation}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </StepDialog>

      {/* Competency Wizard Dialog */}
      <StepDialog
        open={showWizardDialog}
        onOpenChange={(open) => { setShowWizardDialog(open); if (!open) setSettingsStep(0); }}
        title="Competency Program Settings"
        steps={[
          { id: "deadlines", title: "Deadlines", subtitle: "Set training deadlines per level", isComplete: settingsStep > 0 },
          { id: "frequency", title: "Frequency", subtitle: "Configure simulation schedules", isComplete: settingsStep > 1 },
          { id: "kpis", title: "KPIs & Rules", subtitle: "Set completion targets and rules", isComplete: false },
        ]}
        currentStep={settingsStep}
        onStepChange={setSettingsStep}
        onSubmit={handleSaveWizardSettings}
        onCancel={() => { setShowWizardDialog(false); setSettingsStep(0); }}
        submitLabel={saving ? "Saving..." : "Save Settings"}
        submitDisabled={saving}
        saving={saving}
        canAdvance={true}
        maxWidth="max-w-xl"
      >
        {/* Step 0: Deadlines */}
        {settingsStep === 0 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Level 1 Training Deadline (days from hire)</Label>
              <Select
                value={String(wizardSettings.level1_deadline_days)}
                onValueChange={val => setWizardSettings(prev => ({ ...prev, level1_deadline_days: parseInt(val) }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days (Recommended)</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="prerequisite"
                checked={wizardSettings.training_prerequisite_for_approval}
                onChange={e => setWizardSettings(prev => ({ ...prev, training_prerequisite_for_approval: e.target.checked }))}
                className="rounded"
              />
              <Label htmlFor="prerequisite" className="text-sm cursor-pointer">
                Require training completion for Plan Approval
              </Label>
            </div>
          </div>
        )}

        {/* Step 1: Frequency */}
        {settingsStep === 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Level 2 Simulation Frequency</Label>
              <Select
                value={wizardSettings.level2_simulation_frequency}
                onValueChange={val => setWizardSettings(prev => ({ ...prev, level2_simulation_frequency: val }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="bi-annually">Bi-Annually (Recommended)</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Level 3 Simulation Frequency</Label>
              <Select
                value={wizardSettings.level3_simulation_frequency}
                onValueChange={val => setWizardSettings(prev => ({ ...prev, level3_simulation_frequency: val }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="bi-annually">Bi-Annually</SelectItem>
                  <SelectItem value="annually">Annually (Recommended)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Step 2: KPIs */}
        {settingsStep === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Completion Rate Target (%)</Label>
                <Input
                  type="number"
                  min={80}
                  max={100}
                  value={wizardSettings.kpis?.completion_rate_target || 95}
                  onChange={e => setWizardSettings(prev => ({
                    ...prev,
                    kpis: { ...prev.kpis, completion_rate_target: parseInt(e.target.value) }
                  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Recertification Period (months)</Label>
                <Select
                  value={String(wizardSettings.kpis?.recertification_months || 12)}
                  onValueChange={val => setWizardSettings(prev => ({
                    ...prev,
                    kpis: { ...prev.kpis, recertification_months: parseInt(val) }
                  }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                    <SelectItem value="24">24 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </StepDialog>

      {/* Notification Settings Dialog */}
      <Dialog open={showNotificationSettings} onOpenChange={setShowNotificationSettings}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-sky-700" />
              Notification Settings
            </DialogTitle>
            <DialogDescription>
              Configure training reminder and escalation settings
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="notif-enabled"
                checked={notificationSettings.enabled}
                onChange={e => setNotificationSettings(prev => ({ ...prev, enabled: e.target.checked }))}
                className="rounded"
              />
              <Label htmlFor="notif-enabled" className="text-sm cursor-pointer font-medium">
                Enable Training Notifications
              </Label>
            </div>
            
            <div className="space-y-2">
              <Label>Reminder Schedule (days before due date)</Label>
              <div className="flex flex-wrap gap-2">
                {[14, 7, 3, 1].map(day => (
                  <Button
                    key={day}
                    type="button"
                    variant={notificationSettings.reminder_days_before?.includes(day) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      const current = notificationSettings.reminder_days_before || [];
                      const newDays = current.includes(day) 
                        ? current.filter(d => d !== day) 
                        : [...current, day].sort((a, b) => b - a);
                      setNotificationSettings(prev => ({ ...prev, reminder_days_before: newDays }));
                    }}
                  >
                    {day} day{day !== 1 ? 's' : ''}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-slate-500">Selected: {notificationSettings.reminder_days_before?.join(', ') || 'None'} days before deadline</p>
            </div>
            
            <div className="space-y-2">
              <Label>Escalation Schedule (days after due date)</Label>
              <div className="flex flex-wrap gap-2">
                {[1, 3, 7, 14].map(day => (
                  <Button
                    key={day}
                    type="button"
                    variant={notificationSettings.escalation_days_overdue?.includes(day) ? 'destructive' : 'outline'}
                    size="sm"
                    onClick={() => {
                      const current = notificationSettings.escalation_days_overdue || [];
                      const newDays = current.includes(day) 
                        ? current.filter(d => d !== day) 
                        : [...current, day].sort((a, b) => a - b);
                      setNotificationSettings(prev => ({ ...prev, escalation_days_overdue: newDays }));
                    }}
                  >
                    {day} day{day !== 1 ? 's' : ''}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-slate-500">Escalate at: {notificationSettings.escalation_days_overdue?.join(', ') || 'None'} days overdue</p>
            </div>
            
            <div className="space-y-2">
              <Label>Escalation Recipients</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="manager@company.com"
                  value={newEscalationEmail}
                  onChange={e => setNewEscalationEmail(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && addEscalationRecipient()}
                />
                <Button type="button" variant="outline" onClick={addEscalationRecipient}>Add</Button>
              </div>
              {notificationSettings.escalation_recipients?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {notificationSettings.escalation_recipients.map(email => (
                    <Badge key={email} variant="secondary" className="flex items-center gap-1">
                      {email}
                      <XCircle 
                        className="w-3 h-3 cursor-pointer hover:text-red-500" 
                        onClick={() => removeEscalationRecipient(email)}
                      />
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500">These recipients will be notified when training is escalated</p>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="auto-remind"
                checked={notificationSettings.auto_reminder_enabled}
                onChange={e => setNotificationSettings(prev => ({ ...prev, auto_reminder_enabled: e.target.checked }))}
                className="rounded"
              />
              <Label htmlFor="auto-remind" className="text-sm cursor-pointer">
                Enable automatic reminders (requires email integration)
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotificationSettings(false)}>Cancel</Button>
            <Button onClick={handleSaveNotificationSettings} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* V14 D5 — AI Suggest Modules dialog */}
      <Dialog open={showAiSuggestDialog} onOpenChange={(o) => { if (!o) { setAiSuggestions(null); } setShowAiSuggestDialog(o); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI — Suggest Training Modules
            </DialogTitle>
            <DialogDescription>
              Pick a target role and let AI rank the most relevant training modules from your catalogue.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Target role</label>
              <select
                className="w-full mt-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                value={aiSuggestRole}
                onChange={(e) => setAiSuggestRole(e.target.value)}
                data-testid="ai-suggest-role-select"
              >
                <option value="bcm_manager">BCM Manager</option>
                <option value="department_head">Department Head</option>
                <option value="process_manager">Process Manager</option>
                <option value="process_contributor">Process Contributor</option>
                <option value="it_drp_manager">IT-DRP Manager</option>
                <option value="it_function_manager">IT Function Manager</option>
                <option value="it_function_contributor">IT Function Contributor</option>
                <option value="app_manager">App Manager</option>
                <option value="app_contributor">App Contributor</option>
                <option value="executive_sponsor">Executive Sponsor</option>
              </select>
            </div>
            <Button
              onClick={async () => {
                setAiSuggesting(true);
                setAiSuggestions(null);
                try {
                  const r = await api.post('/ai-v14/training/suggest-modules', {
                    target_role: aiSuggestRole,
                    top_n: 5,
                  });
                  setAiSuggestions(r.data || null);
                } catch (e) {
                  toast.error(extractErrorMessage(e, 'AI suggestion failed'));
                } finally {
                  setAiSuggesting(false);
                }
              }}
              disabled={aiSuggesting}
              className="bg-purple-600 hover:bg-purple-700 w-full"
              data-testid="ai-suggest-fetch-btn"
            >
              {aiSuggesting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {aiSuggesting ? 'Analysing…' : 'Get Suggestions'}
            </Button>
            {aiSuggestions && (
              <div className="space-y-2 pt-2 max-h-[50vh] overflow-y-auto">
                {(aiSuggestions.suggestions || []).length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No modules matched this role. Consider adding role-specific training to the catalogue.
                  </p>
                ) : (
                  (aiSuggestions.suggestions || []).map((s, i) => (
                    <div key={i} className="border border-slate-200 rounded-lg p-3" data-testid={`ai-suggestion-${i}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-800">{s.title || s.module_id}</p>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            s.priority === 'high'
                              ? 'bg-rose-100 text-rose-700'
                              : s.priority === 'medium'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {s.priority || 'low'}
                        </span>
                      </div>
                      {s.reason && <p className="text-xs text-slate-500 mt-1">{s.reason}</p>}
                    </div>
                  ))
                )}
                {aiSuggestions.rationale && (
                  <p className="text-[11px] text-slate-400 italic pt-2 border-t border-slate-100">
                    {aiSuggestions.rationale}
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAiSuggestDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Training module review/editor — edit AI content, then publish */}
      <Dialog open={showModuleEditor} onOpenChange={setShowModuleEditor}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="training-module-editor">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-sky-700" />
              Review Training Module
            </DialogTitle>
            <DialogDescription>
              Edit the AI-generated content, then publish to make this module active for training.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={editorForm.title} onChange={(e) => setEditorForm((f) => ({ ...f, title: e.target.value }))} data-testid="module-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input value={editorForm.description} onChange={(e) => setEditorForm((f) => ({ ...f, description: e.target.value }))} data-testid="module-description-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Duration (minutes)</Label>
              <Input type="number" value={editorForm.duration_minutes} onChange={(e) => setEditorForm((f) => ({ ...f, duration_minutes: e.target.value }))} data-testid="module-duration-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Content (Markdown)</Label>
              <Textarea rows={14} value={editorForm.content} onChange={(e) => setEditorForm((f) => ({ ...f, content: e.target.value }))} data-testid="module-content-input" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowModuleEditor(false)} disabled={moduleSaving}>Cancel</Button>
            <Button variant="outline" onClick={() => saveModule(false)} disabled={moduleSaving} data-testid="module-save-draft-btn">Save Draft</Button>
            <Button onClick={() => saveModule(true)} disabled={moduleSaving} data-testid="module-publish-btn">
              {moduleSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              {moduleSaving ? "Saving…" : "Publish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
